import React, { useRef, useEffect, useState } from 'react';
import Bee from './Bee';

// CapTrac logo hexagons, scaled and centered for the animation
const hexagons = [
  // Dark hexagons
  {
    points: '62 40 92 22 122 40 122 76 92 94 62 76 62 40 62 40',
    fill: 'var(--secondary-color)',
    delay: 0
  },
  {
    points: '28.8 -20.4 58.7 -38.6 88.6 -20.4 88.6 15.9 58.7 34.1 28.8 15.9 28.8 -20.4 28.8 -20.4',
    fill: 'var(--secondary-color)',
    delay: 0.12
  },
  {
    points: '95.2 100.5 125.1 82.3 155 100.5 155 136.8 125.1 155 95.2 136.8 95.2 100.5 95.2 100.5',
    fill: 'var(--secondary-color)',
    delay: 0.24
  },
  // Light hexagons
  {
    points: '55.4 76 25.5 94.2 -4.4 76 -4.4 39.7 25.5 21.5 55.4 39.7 55.4 76 55.4 76',
    fill: 'var(--primary-color)',
    delay: 0.18
  },
  {
    points: '88.6 136.8 58.7 155 28.8 136.8 28.8 100.6 58.7 82.4 88.6 100.6 88.6 136.8 88.6 136.8',
    fill: 'var(--primary-color)',
    delay: 0.3
  },
  {
    points: '22.2 136.8 -7.7 155 -37.6 136.8 -37.6 100.6 -7.7 82.4 22.2 100.6 22.2 136.8 22.2 136.8',
    fill: 'var(--primary-color)',
    delay: 0.36
  }
];

// Logo bounding box for bee avoidance
const LOGO_WIDTH = 320;
const LOGO_HEIGHT = 220;
const LOGO_TOP = 0.38; // 38vh

const BORDER_MARGIN = 20;
const MIN_INTERVAL = 2000;
const MAX_INTERVAL = 5000;
const SPEED = 60; // px per second
const TURN_SPEED = 0.08; // radians per frame for smooth turning

const BEE_COUNT = 24;
const ANIMATION_WIDTH = window.innerWidth;
const ANIMATION_HEIGHT = Math.max(window.innerHeight, 700);

function randomAngle() {
  return Math.random() * 2 * Math.PI;
}

function randomInterval() {
  return MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
}

function isNearBorder(x, y, width, height, margin) {
  return (
    x < margin ||
    y < margin ||
    x > width - margin ||
    y > height - margin
  );
}

function angleToLogo(x, y, width, height) {
  const logoCenterX = width / 2;
  const logoCenterY = height * LOGO_TOP + LOGO_HEIGHT / 2;
  return Math.atan2(logoCenterY - y, logoCenterX - x);
}

function distanceToLogo(x, y, width, height) {
  const logoCenterX = width / 2;
  const logoCenterY = height * LOGO_TOP + LOGO_HEIGHT / 2;
  return Math.hypot(logoCenterX - x, logoCenterY - y);
}

function logoAttractChance(x, y, width, height) {
  // Attraction increases with distance, up to 0.95
  const maxDist = Math.hypot(width / 2, height / 2);
  const dist = distanceToLogo(x, y, width, height);
  // Start at 0.3 near logo, up to 0.95 far away
  return Math.min(0.3 + 0.7 * (dist / maxDist), 0.95);
}

const HoneycombAnimation = () => (
  <div className="honeycomb-animation" style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: ANIMATION_HEIGHT,
    minHeight: ANIMATION_HEIGHT,
    minWidth: ANIMATION_WIDTH,
    zIndex: 1,
    pointerEvents: 'none',
    overflow: 'visible',
  }}>
    {/* CapTrac logo centered and lower */}
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', position: 'absolute', left: 0, top: '38vh', zIndex: 10 }}>
      <svg width="320" height="220" viewBox="-50 -50 320 220" style={{ position: 'relative', zIndex: 10, margin: '0 auto' }}>
        {hexagons.map((hex, i) => (
          <polygon
            key={i}
            className="honeycomb-hex"
            style={{
              animationDelay: `${hex.delay}s`,
            }}
            points={hex.points}
            fill={hex.fill}
            opacity="0.88"
          />
        ))}
      </svg>
    </div>
    {/* Animated bees all over the viewport, above the logo */}
    {Array.from({ length: BEE_COUNT }).map((_, i) => (
      <BeeWanderer key={i} width={ANIMATION_WIDTH} height={ANIMATION_HEIGHT} />
    ))}
  </div>
);

const BeeWanderer = ({ width, height }) => {
  const ref = useRef();
  // State: position, direction, target direction, next change time
  const [state] = useState(() => {
    const angle = randomAngle();
    return {
      x: Math.random() * (width - 40) + 20,
      y: Math.random() * (height - 40) + 20,
      dir: angle,
      targetDir: angle,
      nextChange: performance.now() + randomInterval(),
    };
  });
  // Mutable state for animation
  const anim = useRef({ ...state });

  useEffect(() => {
    let frame;
    function animate(ts) {
      const now = ts;
      let { x, y, dir, targetDir, nextChange } = anim.current;
      // Time to change direction?
      if (now > nextChange || isNearBorder(x, y, width, height, BORDER_MARGIN)) {
        if (isNearBorder(x, y, width, height, BORDER_MARGIN)) {
          // Point away from nearest border
          const cx = Math.max(BORDER_MARGIN * 2, Math.min(width - BORDER_MARGIN * 2, x));
          const cy = Math.max(BORDER_MARGIN * 2, Math.min(height - BORDER_MARGIN * 2, y));
          targetDir = Math.atan2(cy - y, cx - x) + (Math.random() - 0.5) * 0.7;
        } else {
          // Chance increases with distance from logo
          const attractChance = logoAttractChance(x, y, width, height);
          if (Math.random() < attractChance) {
            targetDir = angleToLogo(x, y, width, height) + (Math.random() - 0.5) * 0.7;
          } else {
            targetDir = randomAngle();
          }
        }
        nextChange = now + randomInterval();
      }
      // Smoothly turn toward targetDir
      let delta = ((targetDir - dir + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      if (Math.abs(delta) > TURN_SPEED) {
        dir += Math.sign(delta) * TURN_SPEED;
      } else {
        dir = targetDir;
      }
      // Move forward
      x += Math.cos(dir) * SPEED / 60;
      y += Math.sin(dir) * SPEED / 60;
      // Clamp to viewport
      x = Math.max(BORDER_MARGIN, Math.min(width - BORDER_MARGIN, x));
      y = Math.max(BORDER_MARGIN, Math.min(height - BORDER_MARGIN, y));
      anim.current = { x, y, dir, targetDir, nextChange };
      if (ref.current) {
        ref.current.setAttribute('transform', `translate(${x - 19},${y - 19}) rotate(${(dir * 180) / Math.PI + 90},19,19)`);
      }
      frame = requestAnimationFrame(animate);
    }
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [width, height]);
  return (
    <svg
      width={38}
      height={38}
      style={{ position: 'absolute', left: 0, top: 0, zIndex: 20, pointerEvents: 'none' }}
    >
      <g ref={ref}>
        <Bee style={{ transform: 'scale(0.7)' }} />
      </g>
    </svg>
  );
};

export default HoneycombAnimation;
